import { toast } from 'react-toastify';
import ErrorToast from '../components/ErrorToast';

/*
 * How messages behave, for every toast.error / toast.warn in the app (imported once, in index.js, before the app):
 * an error stays until it is closed, so it can be read, copied or screenshotted; clicking it doesn't close it (so
 * its text can be selected); the same error again updates the open one with a count instead of stacking a copy.
 * A warning stays 10 seconds. Success and info keep the container's short default.
 */

const ERROR_OPTIONS = { autoClose: false, closeOnClick: false, draggable: false, className: 'toast-error-sticky' };
const WARNING_OPTIONS = { autoClose: 10000 };

// Error toasts shown, so a message can offer "Close all" when several are open. Whether each is still open is asked
// of react-toastify itself (isActive), which stays right however React mounts and unmounts the toasts.
const shownErrors = new Set();
const listeners = new Set();
const counts = new Map();
const notify = () => listeners.forEach((l) => l());

toast.onChange((item) => {
  if (item.type !== 'error') return;
  if (item.status === 'added') shownErrors.add(item.id);
  if (item.status === 'removed') {
    shownErrors.delete(item.id);
    counts.delete(item.id);
  }
  notify();
});

export const openErrorCount = () => [...shownErrors].filter((id) => toast.isActive(id)).length;
export const subscribeOpenErrors = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export const closeAllErrors = () => {
  toast.clearWaitingQueue();
  [...shownErrors].forEach((id) => toast.dismiss(id));
};

const showError = toast.error;
const showWarning = toast.warn;

toast.error = (content, options = {}) => {
  // Only plain-text messages get the copy/count body; anything else is shown as given, but still stays
  if (typeof content !== 'string' && typeof content !== 'number') {
    return showError(content, { ...ERROR_OPTIONS, ...options });
  }
  const message = String(content);
  const id = options.toastId || `error:${message}`;
  const count = toast.isActive(id) ? (counts.get(id) || 1) + 1 : 1;
  counts.set(id, count);
  const render = <ErrorToast message={message} time={new Date()} count={count} page={window.location.pathname} />;
  if (count > 1) {
    toast.update(id, { render });
    return id;
  }
  shownErrors.add(id);
  return showError(render, { ...ERROR_OPTIONS, ...options, toastId: id });
};

toast.warn = (content, options = {}) => showWarning(content, { ...WARNING_OPTIONS, ...options });
toast.warning = toast.warn;
